// Reset password for an IN-PROGRESS signup (arrived via the Continue-Setup email).
// The draft_id in the URL is the bearer secret (only the inbox owner has it). After a
// successful reset we drop the user straight onto their pending step — and from now on
// the unified login (app.reachiq.ai) also works with the new password.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SIGNUP_PATH,
  PLAN_PATH,
  CREATE_WORKSPACE_PATH,
  VERIFY_EMAIL_PENDING_PATH,
} from "../../utils/constants/routes";
import { signupEndpoints } from "../../utils/constants/httpConstants";
import { POST } from "../../Apis/apiWrapper";
import { saveSignupState, loadSignupState } from "../../utils/helpers/signupState";
import InputField from "../../components/InputFields/InputField";
import Button from "../../components/Buttons/Button";
import ErrorMessage from "../../components/Messages/ErrorMessage";
import { ICON_LABELS } from "../../utils/constants/UI";
import { NEW_LOGIN_REACHIQ_LOGO } from "../../utils/constants/assets";
import AuthAside from "./AuthAside";
import "./onboarding.css";

const STEP_ROUTES = {
  "set-plan": PLAN_PATH,
  "set-subdomain": CREATE_WORKSPACE_PATH,
  "verify-email": VERIFY_EMAIL_PENDING_PATH,
};

const ResetDraftPasswordScreen = () => {
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const draftId = params.get("draft_id") || "";
  const email = params.get("email") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await POST(signupEndpoints.resetDraftPassword, {
        draft_id: draftId,
        password,
        password_confirmation: confirmPassword,
      });
      const data = res?.data?.data || {};
      saveSignupState({
        ...loadSignupState(),
        draft_id: data.draft_id || draftId,
        ...(data.email ? { email: data.email } : {}),
        ...(data.first_name ? { firstName: data.first_name } : {}),
        ...(data.last_name ? { lastName: data.last_name } : {}),
        ...(data.tenant_url ? { tenant_url: data.tenant_url } : {}),
      });
      navigate(STEP_ROUTES[data.step] || SIGNUP_PATH, { replace: true });
    } catch (err) {
      setError(err?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!draftId) {
    navigate(SIGNUP_PATH, { replace: true });
    return null;
  }

  return (
    <div className="onb-root">
      <div className="onb-auth-layout">
        <AuthAside />
        <main className="onb-auth-main">
          <div className="onb-auth-form-area">
            <div className="onb-auth-card">
              <div className="onb-logo-wrap">
                <img src={NEW_LOGIN_REACHIQ_LOGO} alt="ReachIQ" style={{ height: 36 }} />
              </div>
              <h1 style={{ fontSize: 22, margin: "18px 0 6px" }}>Set a new password</h1>
              <p style={{ color: "#6e7684", fontSize: 14, margin: "0 0 18px" }}>
                {email ? `For ${email}. ` : ""}After this you can sign in anytime and continue your setup.
              </p>

              <form onSubmit={handleSubmit} noValidate>
                <div className="onb-field">
                  <label className="onb-label">New Password</label>
                  <InputField
                    size="md40"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    RightIcon={showPassword ? ICON_LABELS.VISIBILITY_ON : ICON_LABELS.VISIBILITY_OFF}
                    rightOnClick={() => setShowPassword((v) => !v)}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  />
                </div>
                <div className="onb-field">
                  <label className="onb-label">Confirm Password</label>
                  <InputField
                    size="md40"
                    type={showPassword ? "text" : "password"}
                    placeholder="Re-enter the password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                  />
                </div>
                <p style={{ color: "#8a8f9c", fontSize: 12.5, margin: "4px 0 12px" }}>
                  8+ characters with an uppercase letter, lowercase letter, number and special character.
                </p>
                {error && (
                  <div role="alert">
                    <ErrorMessage message={error} marginBottom="8px" fontSize="12px" />
                  </div>
                )}
                <Button
                  variant="primary"
                  size="md40"
                  label={loading ? "Saving…" : "Save and continue setup"}
                  disabled={loading}
                />
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResetDraftPasswordScreen;
